import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import type { EChartsOption } from 'echarts';
import { debounce } from 'lodash';

interface DataItem {
  周日期: string;
  产品编码: string;
  产品名称: string;
  品牌名称: string;
  期初库存: number;
  委外在途: number;
  待排产量: number;
  需求量: number;
}

interface SpPlanChartProps {
  data: DataItem[];
  loading?: boolean;
}

// 柱状图颜色
const BAR_COLORS = {
  委外在途: '#5470C6',
  待排产量: '#91CC75',
  需求量: '#EE6666'
};

export const SpPlanChart: React.FC<SpPlanChartProps> = ({ data, loading }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  // 处理数据
  const processData = (rawData: DataItem[]) => {
    if (!rawData || rawData.length === 0) return {
      weeks: [],
      inventory: [],
      inTransit: [],
      planned: [],
      demand: [],
      productInfo: ''
    };

    // 按周日期排序
    const sortedData = [...rawData].sort((a, b) => a.周日期.localeCompare(b.周日期));
    // 去掉周日期中包含的T00:00:00
    const weeks = sortedData.map(item => item.周日期.replace('T00:00:00', ''));
    
    // 提取各个系列的数据
    const inventory = sortedData.map(item => item.期初库存);
    const inTransit = sortedData.map(item => item.委外在途);
    const planned = sortedData.map(item => item.待排产量);
    const demand = sortedData.map(item => -item.需求量); // 负数显示

    // 获取产品信息用于显示
    const productInfo = rawData[0] ? 
      `${rawData[0].产品名称}(${rawData[0].品牌名称})` : '';

    return {
      weeks,
      inventory,
      inTransit,
      planned,
      demand,
      productInfo
    };
  };

  // 初始化图表
  const initChart = () => {
    if (!chartRef.current) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const { weeks, inventory, inTransit, planned, demand, productInfo } = processData(data);

    const option: EChartsOption = {
      title: {
        text: '',
        left: 'center',
        top: 0,
        textStyle: {
          fontSize: 16,
          fontWeight: 'bold'
        },
        subtextStyle: {
          fontSize: 12,
          color: '#666'
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross'
        },
        formatter: (params: any[]) => {
          let result = `${params[0].axisValue}<br/>`;
          params.forEach(param => {
            const marker = `<span style="display:inline-block;margin-right:4px;border-radius:50%;width:10px;height:10px;background-color:${param.color};"></span>`;
            let value = param.value;
            // 如果是需求量，显示正数
            if (param.seriesName === '需求量') {
              value = -value;
            }
            result += `${marker}${param.seriesName}: ${value.toLocaleString()}<br/>`;
          });
          return result;
        }
      },
      legend: {
        data: ['期初库存', '委外在途', '待排产量', '需求量'],
        top: 30,
        left: 'center'
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
        top: 100
      },
      xAxis: {
        type: 'category',
        data: weeks,
        axisLabel: {
          rotate: 45,
          formatter: (value: string) => {
            // 格式化周日期显示
            return value.replace('W', '第') + '周';
          }
        }
      },
      yAxis: {
        type: 'value',
        name: '数量',
        axisLabel: {
          formatter: (value: number) => {
            return value.toLocaleString();
          }
        },
        splitLine: {
          lineStyle: {
            type: 'dashed',
          },
        }
      },
      series: [
        {
          name: '期初库存',
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          data: inventory,
          lineStyle: {
            width: 2
          },
          itemStyle: {
            color: '#73C0DE'
          }
        },
        {
          name: '委外在途',
          type: 'bar',
          stack: 'positive',
          data: inTransit,
          itemStyle: {
            color: BAR_COLORS.委外在途
          }
        },
        {
          name: '待排产量',
          type: 'bar',
          stack: 'positive',
          data: planned,
          itemStyle: {
            color: BAR_COLORS.待排产量
          }
        },
        {
          name: '需求量',
          type: 'bar',
          data: demand,
          itemStyle: {
            color: BAR_COLORS.需求量
          }
        }
      ]
    };

    chartInstance.current.setOption(option);
  };

  // 处理窗口大小变化
  useEffect(() => {
    const handleResize = debounce(() => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    }, 300);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, []);

  // 更新图表数据
  useEffect(() => {
    if (data && data.length > 0) {
      initChart();
    }
  }, [data]);

  // 销毁图表实例
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="w-full h-full min-h-[400px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div ref={chartRef} className="w-full h-[500px]" />
  );
};

export default SpPlanChart;
